<template>
  <div class="mall-h5">
    <!-- 顶部品牌头 -->
    <div class="mh5-banner">
      <div class="mh5-banner__pattern" />
      <div class="mh5-banner__content">
        <img class="mh5-banner__logo-img" src="@/assets/hongJiTang_banner2.png" alt="宏济堂" />
        <div class="mh5-banner__text-group">
          <div class="mh5-banner__sub">百年传承   健康甄选</div>
          <div class="mh5-banner__slogan">品质滋补  放心之选</div>
        </div>
      </div>
      <div class="mh5-banner__decor">
        <span class="mh5-banner__decor-dot" />
        <span class="mh5-banner__decor-dot" />
        <span class="mh5-banner__decor-line" />
      </div>
    </div>

    <!-- 搜索 + 分类 一行 -->
    <div class="mh5-toolbar">
      <div class="mh5-search">
        <van-icon name="search" />
        <input v-model="keyword" placeholder="搜索商品名称" @keyup.enter="onSearch" />
        <span v-if="keyword" class="mh5-search__clear" @click="clearKeyword">
          <van-icon name="clear" />
        </span>
      </div>
      <div class="mh5-cats">
        <div class="mh5-cats__bar" @click="catExpanded = !catExpanded">
          <span class="mh5-cats__current">{{ activeCategory || '全部分类' }}</span>
          <van-icon name="arrow-down" :class="{ 'is-up': catExpanded }" />
        </div>
        <div class="mh5-cats__panel" :class="{ 'is-open': catExpanded }">
          <div
            :class="['mh5-cats__item', { active: activeCategory === '' }]"
            @click="onCatChange('')"
          >全部</div>
          <div
            v-for="c in categories"
            :key="c"
            :class="['mh5-cats__item', { active: activeCategory === c }]"
            @click="onCatChange(c)"
          >{{ c }}</div>
        </div>
      </div>
    </div>

    <!-- 商品网格 -->
    <div class="mh5-grid">
      <div v-if="loading" style="text-align:center;padding:20px"><van-loading /></div>
      <div v-if="!loading && list.length === 0" class="mh5-empty">
        <div class="mh5-empty__icon">
          <van-icon name="goods" />
        </div>
        <div class="mh5-empty__text">暂无商品</div>
        <div class="mh5-empty__hint">换个关键词试试吧</div>
      </div>
      <div v-for="p in list" :key="p.id" class="mh5-card">
        <div class="mh5-card__main" @click="goDetail(p.id)">
          <div class="mh5-card__img">
            <img v-if="p.mainImg" :src="p.mainImg" />
            <div v-else class="mh5-noimg">
              <van-icon name="photo-o" />
            </div>
            <div v-if="isCustom(p)" class="mh5-card__badge">定制</div>
          </div>
          <div class="mh5-card__body">
            <div class="mh5-card__name">{{ displayName(p) }}</div>
            <div class="mh5-card__spec" v-if="p.spec">{{ p.spec }}</div>
          </div>
        </div>
        <div class="mh5-card__foot">
          <div class="mh5-card__price">
            <template v-if="hasDiscount(p)">
              <div class="mh5-price__orig">¥{{ p.retailPrice }}</div>
              <div class="mh5-price__cur">
                <span class="mh5-price__label">新享价</span><span class="mh5-price__sym">¥</span><span class="mh5-price__num">{{ p.discountPrice }}</span>
              </div>
            </template>
            <template v-else>
              <span class="mh5-price__sym">¥</span><span class="mh5-price__num">{{ p.retailPrice }}</span>
            </template>
          </div>
          <button class="mh5-cart-btn" @click="onAddCart(p)">
            <van-icon name="shopping-cart-o" />
          </button>
        </div>
      </div>
    </div>

    <div v-if="list.length && list.length < page.total" class="mh5-more" @click="loadMore">
      <span>查看更多商品</span>
      <van-icon name="arrow-down" />
    </div>

    <!-- 底部安全区 -->
    <div class="mh5-safe-bottom" />

    <!-- 底部 Tab 栏 -->
    <div class="mh5-tabbar">
      <div :class="['mh5-tabbar__item', { active: true }]">
        <van-icon name="home-o" />
        <span>首页</span>
      </div>
      <div class="mh5-tabbar__item" @click="goCart">
        <div class="mh5-tabbar__cart">
          <van-icon name="shopping-cart-o" />
          <span v-if="cartCount > 0" class="mh5-badge">{{ cartCount > 99 ? '99+' : cartCount }}</span>
        </div>
        <span>购物车</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mallH5ProductList, mallH5ProductCategories } from '@/api/mall'
import { addToCart, getCartCount } from './cartStore'
import { ensureWxCode } from './wxAuth'
import { restoreMallCtx, withCtx } from './mallCtx'

export default {
  name: 'MallH5List',
  data() {
    return {
      keyword: '',
      categories: [],
      activeCategory: '',
      catExpanded: false,
      list: [],
      loading: false,
      page: { pageCurrent: 1, pageSize: 20, total: 0 },
      cartCount: 0,
      productId: null,
      saleOrgId: null
    }
  },
  async created() {
    const ctx = restoreMallCtx(this.$route)
    this.productId = ctx.productId
    this.saleOrgId = ctx.saleOrgId
    ensureWxCode()
    this.cartCount = getCartCount()
    this.fetchCategories()
    await this.fetchList()
  },
  methods: {
    hasDiscount(p) {
      return p && p.discountPrice != null && Number(p.discountPrice) < Number(p.retailPrice)
    },
    isCustom(p) {
      return !!(p && p.productName && p.productName.indexOf('【定制品】') >= 0)
    },
    displayName(p) {
      return (p && p.productName || '').replace(/【定制品】/g, '').trim()
    },
    async fetchCategories() {
      try {
        const res = await mallH5ProductCategories()
        this.categories = res.data || []
      } catch (e) { /* ignore */ }
    },
    async fetchList(append = false) {
      this.loading = true
      try {
        const res = await mallH5ProductList({
          pageCurrent: this.page.pageCurrent,
          pageSize: this.page.pageSize,
          productName: this.keyword,
          category: this.activeCategory || undefined,
          productId: this.productId || undefined
        })
        const records = (res.data && res.data.records) || []
        this.list = append ? this.list.concat(records) : records
        this.page.total = (res.data && res.data.total) || 0
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      this.page.pageCurrent = 1
      this.fetchList()
    },
    clearKeyword() {
      this.keyword = ''
      this.page.pageCurrent = 1
      this.fetchList()
    },
    onCatChange(cat) {
      this.activeCategory = cat
      this.catExpanded = false
      this.page.pageCurrent = 1
      this.fetchList()
    },
    loadMore() {
      this.page.pageCurrent += 1
      this.fetchList(true)
    },
    onAddCart(p) {
      addToCart(p, 1)
      this.cartCount = getCartCount()
      this.$toast('已加入购物车')
    },
    goDetail(id) {
      this.$router.push({ path: '/h5/mall/detail', query: withCtx({ id: id }) })
    },
    goCart() {
      this.$router.push({ path: '/h5/mall/cart', query: withCtx() })
    }
  }
}
</script>

<style scoped>
/* ===== 全局基调 ===== */
.mall-h5 {
  min-height: 100vh;
  background: #F8F4F0;
  padding-bottom: 64px;
  -webkit-font-smoothing: antialiased;
}

/* ===== 品牌头 ===== */
.mh5-banner {
  position: relative;
  background: linear-gradient(135deg, #6B1A1A 0%, #8B2222 40%, #5C1010 100%);
  color: #fff;
  padding: 24px 20px 20px;
  overflow: hidden;
}
.mh5-banner__pattern {
  position: absolute;
  top: -40px;
  right: -30px;
  width: 160px;
  height: 160px;
  border: 2px solid rgba(255,255,255,0.08);
  border-radius: 50%;
  pointer-events: none;
}
.mh5-banner__pattern::after {
  content: '';
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  bottom: 20px;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 50%;
}
.mh5-banner__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.mh5-banner__logo-img {
  width: 70%;
  max-width: 280px;
  height: auto;
  display: block;
  filter: brightness(1.05);
}
.mh5-banner__text-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 12px;
}
.mh5-banner__sub {
  font-size: 11px;
  color: rgba(232, 200, 130, 0.75);
  letter-spacing: 4px;
  text-indent: 4px;
}
.mh5-banner__slogan {
  margin-top: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #F0D9A0;
  letter-spacing: 5px;
  text-indent: 5px;
}
.mh5-banner__decor {
  position: absolute;
  bottom: 14px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 1;
}
.mh5-banner__decor-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255,255,255,0.3);
}
.mh5-banner__decor-line {
  width: 24px;
  height: 1px;
  background: rgba(255,255,255,0.2);
  margin-left: 4px;
}

/* ===== 搜索 + 分类 工具栏 ===== */
.mh5-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #F8F4F0;
  position: sticky;
  top: 0;
  z-index: 10;
}
.mh5-search {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 24px;
  padding: 10px 16px;
  flex: 1;
  min-width: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: box-shadow 0.2s;
}
.mh5-search:focus-within {
  box-shadow: 0 2px 12px rgba(139,34,34,0.1);
}
.mh5-search i { color: #B0A89E; font-size: 16px; }
.mh5-search input {
  border: none;
  background: transparent;
  outline: none;
  margin-left: 10px;
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #3C3C3C;
}
.mh5-search input::placeholder { color: #C4BDB3; }
.mh5-search__clear { color: #ccc; padding: 0 2px; cursor: pointer; }

/* ===== 分类筛选 ===== */
.mh5-cats {
  position: relative;
  flex-shrink: 0;
}
.mh5-cats__bar {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  border-radius: 24px;
  padding: 10px 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  cursor: pointer;
  max-width: 108px;
}
.mh5-cats__current {
  font-size: 13px;
  color: #3C3C3C;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mh5-cats__bar i {
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
  transition: transform 0.25s;
}
.mh5-cats__bar i.is-up {
  transform: rotate(180deg);
}
.mh5-cats__panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  width: calc(100vw - 28px);
  transition: max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease;
  padding: 0 12px;
  background: transparent;
}
.mh5-cats__panel.is-open {
  max-height: 300px;
  overflow-y: auto;
  opacity: 1;
  padding: 12px;
  background: #F8F4F0;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
}
.mh5-cats__item {
  padding: 7px 16px;
  border-radius: 18px;
  font-size: 13px;
  color: #666;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: all 0.2s;
  cursor: pointer;
  white-space: nowrap;
}
.mh5-cats__item.active {
  color: #fff;
  background: linear-gradient(135deg, #C0392B, #A3312A);
  box-shadow: 0 2px 8px rgba(192,57,43,0.25);
}

/* ===== 商品网格 ===== */
.mh5-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 12px;
}
.mh5-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(60,40,30,0.05);
  display: flex;
  flex-direction: column;
}
.mh5-card__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  cursor: pointer;
  transition: background 0.15s;
}
.mh5-card__main:active {
  background: #FBF8F5;
}
.mh5-card__img {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 0.82;
  background: #fff;
  overflow: hidden;
  padding: 9px;
  box-sizing: border-box;
}
.mh5-card__img img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.mh5-noimg {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #DFD8CF;
  font-size: 34px;
}
.mh5-card__badge {
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(135deg, #D0493B, #B23128);
  color: #fff;
  font-size: 10px;
  padding: 4px 9px 4px 7px;
  border-radius: 0 0 12px 0;
  font-weight: 600;
  letter-spacing: 1px;
  box-shadow: 0 2px 6px rgba(192,57,43,0.25);
}
.mh5-card__body {
  padding: 10px 12px 6px;
  display: flex;
  flex-direction: column;
  flex: 1;
}
.mh5-card__name {
  font-size: 13px;
  color: #3C3C3C;
  line-height: 1.45;
  height: 38px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  font-weight: 500;
}
.mh5-card__spec {
  font-size: 11px;
  color: #B0A89E;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mh5-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 12px;
  margin-top: auto;
}
.mh5-card__price {
  color: #C0392B;
  line-height: 1;
  min-width: 0;
}
.mh5-price__orig {
  font-size: 11px;
  color: #C4BDB3;
  text-decoration: line-through;
  margin-bottom: 3px;
  font-weight: 400;
}
.mh5-price__cur { line-height: 1; }
.mh5-price__label {
  font-size: 11px;
  font-weight: 500;
  color: #C0392B;
  margin-right: 2px;
}
.mh5-price__sym {
  font-size: 12px;
  font-weight: 600;
}
.mh5-price__num {
  font-size: 18px;
  font-weight: 700;
}
.mh5-cart-btn {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #D0493B, #B23128);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  line-height: 1;
  box-shadow: 0 3px 8px rgba(192,57,43,0.3);
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.mh5-cart-btn i { line-height: 1; }
.mh5-cart-btn:active {
  transform: translateY(1px);
  box-shadow: 0 1px 4px rgba(192,57,43,0.26);
}

/* ===== 空状态 & 加载更多 ===== */
.mh5-empty {
  grid-column: 1 / 3;
  text-align: center;
  padding: 80px 0;
}
.mh5-empty__icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #F0ECE6;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mh5-empty__icon i { font-size: 32px; color: #C4BDB3; }
.mh5-empty__text { font-size: 15px; color: #999; margin-bottom: 6px; }
.mh5-empty__hint { font-size: 12px; color: #C4BDB3; }
.mh5-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  color: #B0A89E;
  font-size: 13px;
  padding: 20px 0 8px;
  cursor: pointer;
}
.mh5-more i { font-size: 12px; }

/* ===== 底部安全区 ===== */
.mh5-safe-bottom { height: 8px; }

/* ===== 底部 Tab 栏 ===== */
.mh5-tabbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 58px;
  background: rgba(255,255,255,0.96);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  box-shadow: 0 -1px 0 rgba(0,0,0,0.04), 0 -4px 16px rgba(0,0,0,0.05);
  z-index: 50;
  padding-bottom: env(safe-area-inset-bottom);
}
.mh5-tabbar__item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: #B0A89E;
  font-size: 11px;
  position: relative;
  transition: color 0.2s;
}
.mh5-tabbar__item i { font-size: 21px; }
.mh5-tabbar__item.active { color: #C0392B; }
.mh5-tabbar__item.active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 22px;
  height: 3px;
  border-radius: 0 0 3px 3px;
  background: linear-gradient(90deg, #C0392B, #D0493B);
}
.mh5-tabbar__item:active { color: #D0493B; }
.mh5-tabbar__cart { position: relative; display: inline-flex; line-height: 0; }
.mh5-badge {
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 17px;
  height: 17px;
  line-height: 17px;
  padding: 0 5px;
  border-radius: 9px;
  background: linear-gradient(135deg, #D0493B, #B23128);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 2px 6px rgba(192,57,43,0.4);
  border: 1.5px solid #fff;
  box-sizing: content-box;
}
</style>
